// AnmeldenPage.jsx — ein Anmeldefenster für alle (Aufgabe 132, 02.10.2026)
//
// Entscheidung Sascha: Admin, Verein, Kampfrichter, Personal, Registrierung
// und Waage melden sich hier an und landen dort, wo sie hingehören. Athleten
// weiter in „Mein TDA“. Die alten Adressen (/einwaage-login, /reg-login,
// /staff-login) leiten hierher.
//
// Geprüft wird parallel: /api/auth/login (Admin/Verein, mit Zwei-Faktor) und
// /api/anmeldung/personal (die vier Turniertag-Konten in einem Aufruf — wegen
// der gemeinsamen Versuchsbremse, siehe backend/routes/anmeldung.js). Passen
// Name und Passwort auf mehrere Konten (gleicher Name in Waage und
// Registrierung), fragt das Fenster einmal „Als was anmelden?“.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vereinsAnmeldungSpeichern, personalAnmeldungSpeichern } from '../lib/anmeldungSpeichern';
import './AnmeldenPage.css';

const SYMBOL = { admin: '🛡️', verein: '🏫', konto: '🔐', kampfrichter: '⚖️', personal: '👥', registrierung: '📝', waage: '⚖' };

async function post(url, body) {
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    return { status: r.status, d: await r.json().catch(() => ({})) };
  } catch { return { status: 0, d: {} }; }
}

export default function AnmeldenPage({ onLogin }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [passwort, setPasswort] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [auswahl, setAuswahl] = useState(null);       // mehrere Konten passen
  const [zwischenToken, setZwischenToken] = useState(null);
  const [code, setCode] = useState('');

  const vereinFertig = (data) => {
    const userData = vereinsAnmeldungSpeichern(data, name.trim());
    if (onLogin) onLogin(userData);          // App.handleLogin → /dashboard bzw. /verein
    else navigate(userData.user.role === 'admin' ? '/dashboard' : '/verein', { replace: true });
  };

  const waehlen = (o) => {
    setFehler('');
    if (o.art === 'admin' || o.art === 'verein' || o.art === 'konto') {
      if (o.daten.zwei_faktor) { setZwischenToken(o.daten.zwischen_token); setAuswahl(null); return; }
      vereinFertig(o.daten);
      return;
    }
    navigate(personalAnmeldungSpeichern(o), { replace: true });
  };

  const absenden = async (e) => {
    e.preventDefault();
    setLaedt(true); setFehler(''); setAuswahl(null);
    const [a, p] = await Promise.all([
      post('/api/auth/login', { email: name.trim(), passwort }),
      post('/api/anmeldung/personal', { benutzername: name.trim(), passwort }),
    ]);
    const optionen = [];
    if (a.status === 200 && a.d.success) {
      // Mit zweitem Faktor verrät die Antwort die Rolle noch nicht — neutral beschriften
      const rolle = a.d.zwei_faktor ? 'konto' : (a.d.user?.role === 'admin' ? 'admin' : 'verein');
      const TITEL = { admin: 'Turnierverwaltung (Admin)', verein: 'Vereinsbereich', konto: 'Verwaltung / Verein' };
      optionen.push({ art: rolle, titel: TITEL[rolle],
        name: a.d.user?.name || a.d.name || name.trim(), daten: a.d });
    }
    if (p.status === 200 && p.d.success) optionen.push(...p.d.treffer);
    setLaedt(false);
    if (optionen.length === 1) return waehlen(optionen[0]);
    if (optionen.length > 1) return setAuswahl(optionen);
    if (a.status === 429 || p.status === 429) return setFehler('Zu viele Versuche. Bitte 15 Minuten warten.');
    if (a.status === 0 && p.status === 0) return setFehler('Keine Verbindung. Bitte erneut versuchen.');
    // Eine sprechende Sperre (Konto aus, Turnier vorbei, Konto gesperrt) vor „falsch“
    const gesperrt = [p, a].find((x) => x.status === 403 || x.status === 423);
    setFehler(gesperrt?.d.error || 'Benutzername oder Passwort stimmt nicht.');
  };

  const codeSenden = async (e) => {
    e.preventDefault();
    setLaedt(true); setFehler('');
    const r = await post('/api/auth/2fa', { zwischen_token: zwischenToken, code: code.trim() });
    setLaedt(false);
    if (r.status === 200 && r.d.success) return vereinFertig(r.d);
    setFehler(r.d.error || 'Code stimmt nicht.');
    if (r.status === 401 && /abgelaufen|Ungültige Anmeldung/i.test(r.d.error || '')) setZwischenToken(null);
  };

  return (
    <div className="anm-seite">
      <div className="anm-karte">
        <div className="anm-kopf">
          <span className="anm-marke">TDA Events</span>
          <h1>Anmelden</h1>
          <p>Für Verwaltung, Vereine, Kampfrichter, Personal, Registrierung und Waage.</p>
        </div>

        {zwischenToken ? (
          <form onSubmit={codeSenden} className="anm-form">
            <p className="anm-hinweis">Dein Konto ist mit einem zweiten Faktor geschützt. Bitte den Code aus der Authenticator-App eingeben.</p>
            <label className="anm-feld">Code
              <input value={code} onChange={(e) => setCode(e.target.value)} autoFocus required inputMode="text" autoComplete="one-time-code" />
            </label>
            {fehler && <div className="anm-fehler">{fehler}</div>}
            <button className="anm-knopf" disabled={laedt}>{laedt ? 'Wird geprüft…' : 'Bestätigen'}</button>
            <button type="button" className="anm-link" onClick={() => { setZwischenToken(null); setCode(''); }}>Zurück</button>
          </form>
        ) : auswahl ? (
          <div className="anm-form">
            <p className="anm-hinweis">Name und Passwort passen auf mehrere Zugänge. Als was möchtest du dich anmelden?</p>
            {auswahl.map((o) => (
              <button key={o.art} className="anm-wahl" onClick={() => waehlen(o)}>
                <span className="anm-wahl-symbol">{SYMBOL[o.art] || '•'}</span>
                <span><strong>{o.titel}</strong><br /><span className="anm-grau">{o.name}</span></span>
              </button>
            ))}
            <button type="button" className="anm-link" onClick={() => setAuswahl(null)}>Zurück</button>
          </div>
        ) : (
          <form onSubmit={absenden} className="anm-form">
            <label className="anm-feld">Benutzername oder E-Mail
              <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="username" autoCapitalize="none" required autoFocus />
            </label>
            <label className="anm-feld">Passwort
              <input type="password" value={passwort} onChange={(e) => setPasswort(e.target.value)} autoComplete="current-password" required />
            </label>
            {fehler && <div className="anm-fehler">{fehler}</div>}
            <button className="anm-knopf" disabled={laedt}>{laedt ? 'Wird geprüft…' : 'Anmelden'}</button>
            <p className="anm-grau anm-fuss">Athleten melden sich in <a href="https://my.tda-intl.org">Mein TDA</a> an.</p>
          </form>
        )}
      </div>
    </div>
  );
}
